import { useContext } from "react";
import { Formik, Form, FieldArray, Field } from "formik";
import Input from "../_local/components/Input/index";
import Dropdown from "../_local/components/Dropdown/index";
import ImageUpload from "../_local/components/ImageUpload/index";
import FormWrapper from "../_local/components/FormWrapper/index";
import ResetAndSubmit from "../_local/components/ResetAndSubmit/index";
import * as Yup from "yup";
import TextArea from "../_local/components/TextArea/index";
import { AppContext } from "../_local/state/index";
import { getImageValidationSchema } from "../_local/constants/validationSchemas/index";
import Card from "../_local/components/OnboardingFormCard/index";
import ImageHandler from "./ImageHandler";
import MultiImageUpload from "../_local/components/ImageUpload/MultipleImageUpload";

// Comprehensive validation schema
const validationSchema = Yup.object({
  campus: Yup.array().of(
    Yup.object({
      ABOUT_CAMPUS: Yup.string().trim(), // Optional
      IMAGE_FOR: Yup.string().required("Image For is required"),
      CAMPUS_IMAGE: Yup.array().when("ABOUT_CAMPUS", {
        is: (value) => !!value && value.trim() !== "",
        then: (schema) =>
          schema
            .min(1, "At least one image is required")
            .max(5, "Maximum 5 images allowed"),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
  ),
});

const NewCampusInfo = ({
  handleNavigate,
  resetFormData,
  disableForm,
}: {
  handleNavigate;
  resetFormData?: any;
  titleAndDesNotRequired?: boolean;
  disableForm?: boolean;
}) => {
  const { globalData } = useContext(AppContext);
  const emptyCampusData = {
    DRAWER: false,
    ABOUT_CAMPUS: "",
    IMAGE_FOR: "",
    CAMPUS_IMAGE: [],
  };

  // Load initial data from session storage or create default
  const loadInitialData = () => {
    const savedData = JSON.parse(sessionStorage.getItem("campus")) || [];
    return savedData.length
      ? savedData.map((item) => ({
          ...item,
          CAMPUS_IMAGE: Array.isArray(item.CAMPUS_IMAGE)
            ? item.CAMPUS_IMAGE
            : [],
        }))
      : [
          "Institution Buildings",
          "Classrooms",
          "Auditorium",
          "Playground",
          "Extra Circular/Facilities",
        ].map((item) => ({
          ABOUT_CAMPUS: "",
          IMAGE_FOR: item,
          CAMPUS_IMAGE: [],
        }));
  };

  console.log("Initial Campus Data:", loadInitialData());

  return (
    <Formik
      initialValues={{ campus: loadInitialData() }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => {
        console.log("Submission Values:", values);
        // Save the campus info to session storage
        sessionStorage.setItem("campus", JSON.stringify(values.campus));

        // Navigate to the next step
        handleNavigate();
      }}
    >
      {({
        values,
        handleSubmit,
        resetForm,
        errors,
        setFieldValue,
        isSubmitting,
      }) => (
        <FormWrapper
          title="Showcase Your Institute Campus Excellence"
          description="Upload photos of your institution's vibrant spaces-from classrooms to playgrounds-and let us highlight your institution's environment with stunning visuals."
          subHeading="Upload images min 1 and max 5"
          childern={
            <Form onSubmit={handleSubmit}>
              <div>
                <div className="rounded-2xl my-1 mx-1">
                  <FieldArray name="campus">
                    {({ push, remove, form }) => (
                      <div className="flex flex-col space-y-4 mb-4">
                        {values.campus.map((campusElement, index) => (
                          <Card
                            key={`campus-${index}`} // Ensure unique key
                            isError={Boolean(errors?.campus?.[index])}
                            index={index}
                            handleOpenDrawer={() =>
                              form.setFieldValue(`campus.${index}.DRAWER`, true)
                            }
                            handleCloseDrawer={() =>
                              form.setFieldValue(
                                `campus.${index}.DRAWER`,
                                false
                              )
                            }
                            onDelete={() => {
                              if (values.campus.length > 1) {
                                remove(index);
                              } else {
                                // Prevent removing the last entry
                                alert("At least one campus info must remain.");
                              }
                            }}
                            element={campusElement}
                            children={
                              <div className="flex flex-wrap gap-x-2 gap-y-4">
                                <div className="w-full">
                                  <label className="block text-sm font-medium mb-1">
                                    Upload Campus Images (Max 5)
                                  </label>
                                  <div className="flex flex-col mt-5">
                                    <Field
                                      name={`campus.${index}.CAMPUS_IMAGE`}
                                    >
                                      {({ field, form }) => (
                                        <MultiImageUpload
                                          disable={
                                            disableForm ||
                                            campusElement?.CAMPUS_IMAGE
                                              ?.length >= 5
                                          }
                                          imageValue={field.value}
                                          onChange={(item) => {
                                            const currentImages = Array.isArray(
                                              campusElement.CAMPUS_IMAGE
                                            )
                                              ? campusElement.CAMPUS_IMAGE
                                              : [];
                                            const newImages = [
                                              ...currentImages,
                                              item,
                                            ];
                                            form.setFieldValue(
                                              `campus.${index}.CAMPUS_IMAGE`,
                                              newImages
                                            );
                                          }}
                                        />
                                      )}
                                    </Field>
                                    <div className="mt-4">
                                      <ImageHandler
                                        disable={disableForm}
                                        campusElement={campusElement}
                                        setCampusElement={(updatedImages) => {
                                          setFieldValue(`campus.${index}`, {
                                            ...campusElement,
                                            CAMPUS_IMAGE: updatedImages,
                                          });
                                        }}
                                      />
                                    </div>
                                    {/* {(index === 0) && <div className="w-full mt-4">
                                      <TextArea
                                        disabled={disableForm}
                                        label="Campus Description?"
                                        name={`campus.${0}.ABOUT_CAMPUS`}
                                        autoGenerate={true}
                                        generate={{
                                          "category": "faculty",
                                          "kwargs": {
                                            "name": campusElement?.IMAGE_FOR,
                                          }
                                        }}
                                      />
                                    </div>} */}
                                  </div>
                                </div>
                              </div>
                            }
                          />
                        ))}
                      </div>
                    )}
                  </FieldArray>
                </div>
              </div>
              {!disableForm && (
                <ResetAndSubmit
                  submitToolTip={
                    !values?.campus?.some(
                      (item) =>
                        !!item?.ABOUT_CAMPUS ||
                        (Array.isArray(item?.CAMPUS_IMAGE) &&
                          item?.CAMPUS_IMAGE?.length > 0)
                    )
                      ? "Please fill in at least one campus info section with details or upload at least one image"
                      : Boolean(errors?.campus)
                      ? "Please fill in all required fields"
                      : ""
                  }
                  isError={
                    !values?.campus?.some(
                      (item) =>
                        !!item?.ABOUT_CAMPUS ||
                        (Array.isArray(item?.CAMPUS_IMAGE) &&
                          item?.CAMPUS_IMAGE?.length > 0)
                    ) || Boolean(errors?.campus)
                  }
                  resetForm={() => {
                    resetForm();
                    sessionStorage.removeItem("campus");
                    resetFormData();
                  }}
                />
              )}
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default NewCampusInfo;
